// AudiologyScreeningForm.tsx
import React, { useEffect, useState } from "react";
import "../../../center admin css/AudiologyScreeningForm.css";
import api from "../../../api/axiosConfig"; // ✅ use your central API config
import "../../../center admin css/centerAdmin.css";
import { useS3Uploader } from "../../../hooks/useS3Uploader"; // <-- Added

interface FormComponentProps {
  emrId: string;
  onComplete?: () => void;
}

type EarSide = "left" | "right";

export type Otoscopy = {
  healthy?: "" | "yes" | "no";
  reasons: {
    bulging?: boolean;
    perforated?: boolean;
    retracted?: boolean;
    others?: boolean;
    pleaseSpecify?: string;
  };
  specify?: string;
};

export type PTAResult = "Pass" | "Fail" | "";
export type AudiometryResult = "Pass" | "Fail" | "";
export type PTAFreqRow = {
  frequency: string;
  intensity?: "20 dBHL" | "40 dBHL" | "";
  rightEar: PTAResult;
  leftEar: PTAResult;
};

export type OAEBlock = {
  totalOAE?: string;
  totalNoise?: string;
  stability?: string;
  reproducibility?: string;
  freqRange?: string;
  result?: "Pass" | "Fail" | "";
};

export type DPOAEBlock = {
  totalOAE?: string;
  percentStimulusOK?: string;
  dpLevels?: string;
  startFrequency?: string;
  endFrequency?: string;
  resolution?: string;
  result?: "Pass" | "Fail" | "";
};

export type TympBlock = {
  type?: string;
  earCanalVolume?: string;
  middleEarPressureDaPa?: string;
  staticCompliance?: string;
  result?: "Pass" | "Fail" | "";
};

export type AabrBeraBlock = {
  impedance?: string;
  stimulusLevel?: string;
  peakVLatency?: string;
  result?: "Pass" | "Fail" | "";
};

export type Summary = Record<EarSide, "Pass" | "Fail" | "Rescreen" | "">;

export type AudiologyFormData = {
  // audiometryResults: any;
  otoscopy: Record<EarSide, Otoscopy>;
  ptaTable: PTAFreqRow[];
  audiometryResults: Record<EarSide, AudiometryResult>;
  teoae: Record<EarSide, OAEBlock>;
  dpoae: Record<EarSide, DPOAEBlock>;
  tymp: Record<EarSide, TympBlock>;
  aabr: Record<EarSide, AabrBeraBlock>;
  summary: Summary;
  deviceReport?: File | null;
};

export const AudiologyScreeningForm: React.FC<FormComponentProps> = ({
  emrId,
  onComplete,
}) => {
  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState<Record<string, boolean>>({});

  const [expandedSections, setExpandedSections] = useState({
    otoscopy: true,
    pta: true,
    teoae: true,
    dpoae: true,
    tymp: true,
    aabr: true,
    summary: true,
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // ⬅️ NEW: S3 uploader hook
  const { uploadPdf, uploading: s3Uploading } = useS3Uploader(emrId);

  const [deviceReports, setDeviceReports] = useState<File[]>([]); // <-- MULTIPLE FILES
  const [ptaReports, setPtaReports] = useState<File[]>([]);
  const [teoaeReports, setTeoaeReports] = useState<File[]>([]);
  const [dpoaeReports, setDpoaeReports] = useState<File[]>([]);
  const [tympReports, setTympReports] = useState<File[]>([]);
  const [aabrReports, setAabrReports] = useState<File[]>([]);
  const [finalRecommendation, setFinalRecommendation] = useState("");

  const [existingReportUrls, setExistingReportUrls] = useState({
    device: [] as string[],
    teoae: [] as string[],
    dpoae: [] as string[],
    tymp: [] as string[],
    aabr: [] as string[],
  });

  const getExistingFileName = (key: string) => {
    return decodeURIComponent(
      key.split("/").pop() || "Previously uploaded PDF",
    );
  };

  const [formData, setFormData] = useState<AudiologyFormData>({
    otoscopy: {
      left: { healthy: "", reasons: {} },
      right: { healthy: "", reasons: {} },
    },

    ptaTable: [
      { frequency: "500 Hz", intensity: "20 dBHL", rightEar: "", leftEar: "" },
      { frequency: "1000 Hz", intensity: "20 dBHL", rightEar: "", leftEar: "" },
      { frequency: "2000 Hz", intensity: "20 dBHL", rightEar: "", leftEar: "" },
      { frequency: "4000 Hz", intensity: "20 dBHL", rightEar: "", leftEar: "" },
      { frequency: "500 Hz", intensity: "40 dBHL", rightEar: "", leftEar: "" },
      { frequency: "1000 Hz", intensity: "40 dBHL", rightEar: "", leftEar: "" },
      { frequency: "2000 Hz", intensity: "40 dBHL", rightEar: "", leftEar: "" },
      { frequency: "4000 Hz", intensity: "40 dBHL", rightEar: "", leftEar: "" },
    ],
    audiometryResults: {
      left: "",
      right: "",
    },
    teoae: { left: {}, right: {} },
    dpoae: { left: {}, right: {} },
    tymp: { left: {}, right: {} },
    aabr: { left: {}, right: {} },
    summary: { left: "", right: "" },
    deviceReport: null,
  });

  const resetForm = () => {
    setErrors({});

    setFinalRecommendation("");

    setDeviceReports([]);
    setPtaReports([]);
    setTeoaeReports([]);
    setDpoaeReports([]);
    setTympReports([]);
    setAabrReports([]);

    setExistingReportUrls({
      device: [],
      teoae: [],
      dpoae: [],
      tymp: [],
      aabr: [],
    });

    setFormData({
      otoscopy: {
        left: { healthy: "", reasons: {} },
        right: { healthy: "", reasons: {} },
      },
      ptaTable: [
        {
          frequency: "500 Hz",
          intensity: "20 dBHL",
          rightEar: "",
          leftEar: "",
        },
        {
          frequency: "1000 Hz",
          intensity: "20 dBHL",
          rightEar: "",
          leftEar: "",
        },
        {
          frequency: "2000 Hz",
          intensity: "20 dBHL",
          rightEar: "",
          leftEar: "",
        },
        {
          frequency: "4000 Hz",
          intensity: "20 dBHL",
          rightEar: "",
          leftEar: "",
        },

        {
          frequency: "500 Hz",
          intensity: "40 dBHL",
          rightEar: "",
          leftEar: "",
        },
        {
          frequency: "1000 Hz",
          intensity: "40 dBHL",
          rightEar: "",
          leftEar: "",
        },
        {
          frequency: "2000 Hz",
          intensity: "40 dBHL",
          rightEar: "",
          leftEar: "",
        },
        {
          frequency: "4000 Hz",
          intensity: "40 dBHL",
          rightEar: "",
          leftEar: "",
        },
      ],
      audiometryResults: {
        left: "",
        right: "",
      },
      teoae: { left: {}, right: {} },
      dpoae: { left: {}, right: {} },
      tymp: { left: {}, right: {} },
      aabr: { left: {}, right: {} },
      summary: { left: "", right: "" },
      deviceReport: null,
    });
  };

  const handlePickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const valid = files.filter((f) => f.type === "application/pdf");

    if (valid.length === 0) return;

    setDeviceReports((prev) => [...prev, ...valid]);
  };

  const loadExistingAudiology = async () => {
    if (!emrId) return;

    try {
      const res = await api.dataCollectionApi.get(
        `/api/forms/audiology/${encodeURIComponent(emrId)}`,
      );

      const saved = res.data?.data;

      if (!saved) {
        resetForm();
        return;
      }

      const otoscopy = saved.otoscopicExamination ?? {};
      const pta = saved.pureToneAudiometry ?? [];
      const audiometryResults = saved.audiometryResults ?? {};
      const teoae = saved.teoaeTests ?? {};
      const dpoae = saved.dpoaeTests ?? {};
      const tymp = saved.tympanometry ?? {};
      const aabr = saved.aabrBeraTest ?? {};
      const summary = saved.finalSummary ?? {};
      setFinalRecommendation(saved.finalRecommendation ?? "");

      setFormData({
        otoscopy: {
          right: {
            healthy:
              otoscopy.rightEar?.healthy === true
                ? "yes"
                : otoscopy.rightEar?.healthy === false
                  ? "no"
                  : "",
            reasons: {
              bulging: !!otoscopy.rightEar?.reasons?.bulging,
              perforated: !!otoscopy.rightEar?.reasons?.perforated,
              retracted: !!otoscopy.rightEar?.reasons?.retracted,
              others: !!otoscopy.rightEar?.reasons?.others,
              pleaseSpecify:
                otoscopy.rightEar?.reasons?.pleaseSpecify || undefined,
            },
            specify: otoscopy.rightEar?.reasons?.pleaseSpecify || "",
          },

          left: {
            healthy:
              otoscopy.leftEar?.healthy === true
                ? "yes"
                : otoscopy.leftEar?.healthy === false
                  ? "no"
                  : "",
            reasons: {
              bulging: !!otoscopy.leftEar?.reasons?.bulging,
              perforated: !!otoscopy.leftEar?.reasons?.perforated,
              retracted: !!otoscopy.leftEar?.reasons?.retracted,
              others: !!otoscopy.leftEar?.reasons?.others,
              pleaseSpecify:
                otoscopy.leftEar?.reasons?.pleaseSpecify || undefined,
            },
            specify: otoscopy.leftEar?.reasons?.pleaseSpecify || "",
          },
        },

        ptaTable: (
          [
            {
              frequency: "500 Hz",
              intensity: "20 dBHL",
              rightEar: "",
              leftEar: "",
            },
            {
              frequency: "1000 Hz",
              intensity: "20 dBHL",
              rightEar: "",
              leftEar: "",
            },
            {
              frequency: "2000 Hz",
              intensity: "20 dBHL",
              rightEar: "",
              leftEar: "",
            },
            {
              frequency: "4000 Hz",
              intensity: "20 dBHL",
              rightEar: "",
              leftEar: "",
            },
            {
              frequency: "500 Hz",
              intensity: "40 dBHL",
              rightEar: "",
              leftEar: "",
            },
            {
              frequency: "1000 Hz",
              intensity: "40 dBHL",
              rightEar: "",
              leftEar: "",
            },
            {
              frequency: "2000 Hz",
              intensity: "40 dBHL",
              rightEar: "",
              leftEar: "",
            },
            {
              frequency: "4000 Hz",
              intensity: "40 dBHL",
              rightEar: "",
              leftEar: "",
            },
          ] as PTAFreqRow[]
        ).map((row, index) => ({
          ...row,
          rightEar: pta[index]?.rightEar ?? "",
          leftEar: pta[index]?.leftEar ?? "",
        })),

        audiometryResults: {
          left:
            audiometryResults.leftEar === true
              ? "Pass"
              : audiometryResults.leftEar === false
                ? "Fail"
                : "",
          right:
            audiometryResults.rightEar === true
              ? "Pass"
              : audiometryResults.rightEar === false
                ? "Fail"
                : "",
        },

        teoae: {
          left: {
            totalOAE:
              teoae.leftEar?.totalOAE !== undefined
                ? String(teoae.leftEar.totalOAE)
                : "",
            totalNoise:
              teoae.leftEar?.totalNoise !== undefined
                ? String(teoae.leftEar.totalNoise)
                : "",
            stability:
              teoae.leftEar?.stability !== undefined
                ? String(teoae.leftEar.stability)
                : "",
            reproducibility:
              teoae.leftEar?.reproducibility !== undefined
                ? String(teoae.leftEar.reproducibility)
                : "",
            freqRange: teoae.leftEar?.frequencyRange ?? "",
            result: teoae.leftEar?.result ?? "",
          },

          right: {
            totalOAE:
              teoae.rightEar?.totalOAE !== undefined
                ? String(teoae.rightEar.totalOAE)
                : "",
            totalNoise:
              teoae.rightEar?.totalNoise !== undefined
                ? String(teoae.rightEar.totalNoise)
                : "",
            stability:
              teoae.rightEar?.stability !== undefined
                ? String(teoae.rightEar.stability)
                : "",
            reproducibility:
              teoae.rightEar?.reproducibility !== undefined
                ? String(teoae.rightEar.reproducibility)
                : "",
            freqRange: teoae.rightEar?.frequencyRange ?? "",
            result: teoae.rightEar?.result ?? "",
          },
        },

        dpoae: {
          left: {
            totalOAE:
              dpoae.leftEar?.totalOAE !== undefined
                ? String(dpoae.leftEar.totalOAE)
                : "",
            percentStimulusOK:
              dpoae.leftEar?.percentStimulusOK !== undefined
                ? String(dpoae.leftEar.percentStimulusOK)
                : "",
            dpLevels: dpoae.leftEar?.dpLevels ?? "",
            startFrequency:
              dpoae.leftEar?.startFrequency !== undefined
                ? String(dpoae.leftEar.startFrequency)
                : "",
            endFrequency:
              dpoae.leftEar?.endFrequency !== undefined
                ? String(dpoae.leftEar.endFrequency)
                : "",
            resolution: dpoae.leftEar?.resolution ?? "",
            result: dpoae.leftEar?.result ?? "",
          },

          right: {
            totalOAE:
              dpoae.rightEar?.totalOAE !== undefined
                ? String(dpoae.rightEar.totalOAE)
                : "",
            percentStimulusOK:
              dpoae.rightEar?.percentStimulusOK !== undefined
                ? String(dpoae.rightEar.percentStimulusOK)
                : "",
            dpLevels: dpoae.rightEar?.dpLevels ?? "",
            startFrequency:
              dpoae.rightEar?.startFrequency !== undefined
                ? String(dpoae.rightEar.startFrequency)
                : "",
            endFrequency:
              dpoae.rightEar?.endFrequency !== undefined
                ? String(dpoae.rightEar.endFrequency)
                : "",
            resolution: dpoae.rightEar?.resolution ?? "",
            result: dpoae.rightEar?.result ?? "",
          },
        },

        tymp: {
          left: {
            type: tymp.leftEar?.type ?? "",
            earCanalVolume:
              tymp.leftEar?.earCanalVolume !== undefined
                ? String(tymp.leftEar.earCanalVolume)
                : "",
            middleEarPressureDaPa:
              tymp.leftEar?.middleEarPressureDaPa !== undefined
                ? String(tymp.leftEar.middleEarPressureDaPa)
                : "",
            staticCompliance:
              tymp.leftEar?.staticCompliance !== undefined
                ? String(tymp.leftEar.staticCompliance)
                : "",
            result: tymp.leftEar?.result ?? "",
          },

          right: {
            type: tymp.rightEar?.type ?? "",
            earCanalVolume:
              tymp.rightEar?.earCanalVolume !== undefined
                ? String(tymp.rightEar.earCanalVolume)
                : "",
            middleEarPressureDaPa:
              tymp.rightEar?.middleEarPressureDaPa !== undefined
                ? String(tymp.rightEar.middleEarPressureDaPa)
                : "",
            staticCompliance:
              tymp.rightEar?.staticCompliance !== undefined
                ? String(tymp.rightEar.staticCompliance)
                : "",
            result: tymp.rightEar?.result ?? "",
          },
        },

        aabr: {
          left: {
            impedance:
              aabr.leftEar?.impedance !== undefined
                ? String(aabr.leftEar.impedance)
                : "",
            stimulusLevel:
              aabr.leftEar?.stimulusLevel !== undefined
                ? String(aabr.leftEar.stimulusLevel)
                : "",
            peakVLatency:
              aabr.leftEar?.peakVLatency !== undefined
                ? String(aabr.leftEar.peakVLatency)
                : "",
            result: aabr.leftEar?.result ?? "",
          },

          right: {
            impedance:
              aabr.rightEar?.impedance !== undefined
                ? String(aabr.rightEar.impedance)
                : "",
            stimulusLevel:
              aabr.rightEar?.stimulusLevel !== undefined
                ? String(aabr.rightEar.stimulusLevel)
                : "",
            peakVLatency:
              aabr.rightEar?.peakVLatency !== undefined
                ? String(aabr.rightEar.peakVLatency)
                : "",
            result: aabr.rightEar?.result ?? "",
          },
        },

        summary: {
          left: summary.leftEar ?? "",
          right: summary.rightEar ?? "",
        },

        deviceReport: null,
      });

      setExistingReportUrls({
        device: saved.deviceReportUrls ?? [],
        teoae: teoae.reportUrls ?? [],
        dpoae: dpoae.reportUrls ?? [],
        tymp: tymp.reportUrls ?? [],
        aabr: aabr.reportUrls ?? [],
      });
    } catch (err: any) {
      if (err?.response?.status === 404) {
        resetForm();
        return;
      }

      console.error("Failed to load existing Audiology:", err);
    }
  };

  useEffect(() => {
    loadExistingAudiology();
  }, [emrId]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files || []);
    const pdfs = files.filter((f) => f.type === "application/pdf");

    if (pdfs.length > 0) {
      setDeviceReports((prev) => [...prev, ...pdfs]);
    }
  };

  const removeFile = (index: number) => {
    setDeviceReports((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSectionFilePick = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: React.Dispatch<React.SetStateAction<File[]>>,
  ) => {
    const files = Array.from(e.target.files || []);

    const valid = files.filter(
      (f) => f.type === "application/pdf" || f.type.startsWith("image/"),
    );

    if (valid.length > 0) {
      setter((prev) => [...prev, ...valid]);
    }
  };

  const removeSectionFile = (
    index: number,
    setter: React.Dispatch<React.SetStateAction<File[]>>,
  ) => {
    setter((prev) => prev.filter((_, i) => i !== index));
  };

  // ---------- Helper mutators (add these before handleSubmit) ----------
  const setOtoHealthy = (ear: EarSide, v: "" | "yes" | "no") =>
    setFormData((p) => ({
      ...p,
      otoscopy: {
        ...p.otoscopy,
        [ear]: { ...p.otoscopy[ear], healthy: v },
      },
    }));

  const toggleOtoReason = (ear: EarSide, reason: keyof Otoscopy["reasons"]) =>
    setFormData((p) => ({
      ...p,
      otoscopy: {
        ...p.otoscopy,
        [ear]: {
          ...p.otoscopy[ear],
          reasons: {
            ...p.otoscopy[ear].reasons,
            [reason]: !p.otoscopy[ear].reasons[reason],
          },
        },
      },
    }));

  const setOtoSpecify = (ear: EarSide, value: string) =>
    setFormData((p) => ({
      ...p,
      otoscopy: {
        ...p.otoscopy,
        [ear]: { ...p.otoscopy[ear], specify: value },
      },
    }));

  const setPta = (
    idx: number,
    ear: "rightEar" | "leftEar",
    result: PTAResult,
  ) =>
    setFormData((p) => ({
      ...p,
      ptaTable: p.ptaTable.map((r, i) =>
        i === idx ? { ...r, [ear]: result } : r,
      ),
    }));

  const setAudiometryResult = (ear: EarSide, result: AudiometryResult) =>
    setFormData((p) => ({
      ...p,
      audiometryResults: {
        ...p.audiometryResults,
        [ear]: result,
      },
    }));

  const setOAEField = (
    block: "teoae" | "dpoae",
    ear: EarSide,
    field: string,
    val: string,
  ) =>
    setFormData((p) => ({
      ...p,
      [block]: {
        ...p[block],
        [ear]: { ...p[block][ear], [field]: val },
      },
    }));

  const setOAEResult = (
    block: "teoae" | "dpoae",
    ear: EarSide,
    val: "Pass" | "Fail",
  ) =>
    setFormData((p) => ({
      ...p,
      [block]: {
        ...p[block],
        [ear]: { ...p[block][ear], result: val },
      },
    }));

  const setTympField = (ear: EarSide, field: keyof TympBlock, val: string) =>
    setFormData((p) => ({
      ...p,
      tymp: {
        ...p.tymp,
        [ear]: { ...p.tymp[ear], [field]: val },
      },
    }));

  const setTympResult = (ear: EarSide, val: "Pass" | "Fail") =>
    setFormData((p) => ({
      ...p,
      tymp: {
        ...p.tymp,
        [ear]: { ...p.tymp[ear], result: val },
      },
    }));

  const setAabrField = (
    ear: EarSide,
    field: keyof AabrBeraBlock,
    val: string,
  ) =>
    setFormData((p) => ({
      ...p,
      aabr: {
        ...p.aabr,
        [ear]: { ...p.aabr[ear], [field]: val },
      },
    }));

  const setAabrResult = (ear: EarSide, val: "Pass" | "Fail") =>
    setFormData((p) => ({
      ...p,
      aabr: {
        ...p.aabr,
        [ear]: { ...p.aabr[ear], result: val },
      },
    }));

  const setSummary = (ear: EarSide, val: "Pass" | "Fail" | "Rescreen") =>
    setFormData((p) => ({
      ...p,
      summary: { ...p.summary, [ear]: val },
    }));

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (err) => reject(err);
    });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // ==========================
    // 1. OTOSCOPY REQUIRED
    // ==========================

    if (!formData.otoscopy.left.healthy || !formData.otoscopy.right.healthy) {
      setErrors({
        otoscopy: true,
      });

      setLoading(false);

      alert("Please complete Otoscopic Examination");
      return;
    }

    // ==========================
    // 1A. OTOSCOPY "OTHERS" REQUIRED
    // ==========================

    const leftOthersMissing =
      formData.otoscopy.left.reasons.others &&
      !(
        formData.otoscopy.left.specify?.trim() ||
        formData.otoscopy.left.reasons.pleaseSpecify?.trim()
      );

    const rightOthersMissing =
      formData.otoscopy.right.reasons.others &&
      !(
        formData.otoscopy.right.specify?.trim() ||
        formData.otoscopy.right.reasons.pleaseSpecify?.trim()
      );

    if (leftOthersMissing || rightOthersMissing) {
      setErrors({
        otoscopy: true,
      });

      setLoading(false);

      alert('Please specify the "Others" reason for the selected ear.');
      return;
    }

    // ==========================
    // 2. PTA REQUIRED
    // ==========================

    // ==========================
    // 3. FINAL SUMMARY REQUIRED
    // ==========================

    if (!formData.summary.left || !formData.summary.right) {
      setErrors({
        summary: true,
      });

      setLoading(false);

      alert("Please complete Final Summary");
      return;
    }

    // ==========================
    // TEOAE VALIDATION
    // ==========================

    const teoaeFields = [
      formData.teoae.left.totalOAE,
      formData.teoae.left.totalNoise,
      formData.teoae.left.stability,
      formData.teoae.left.reproducibility,
      formData.teoae.left.freqRange,
      formData.teoae.left.result,

      formData.teoae.right.totalOAE,
      formData.teoae.right.totalNoise,
      formData.teoae.right.stability,
      formData.teoae.right.reproducibility,
      formData.teoae.right.freqRange,
      formData.teoae.right.result,
    ];

    const teoaeStarted = teoaeFields.some((v) => v !== undefined && v !== "");

    const teoaeIncomplete = teoaeFields.some(
      (v) => v === undefined || v === "",
    );

    if (teoaeStarted && teoaeIncomplete) {
      setErrors({
        teoae: true,
      });

      setLoading(false);

      alert("Please complete all TEOAE fields");
      return;
    }

    // ==========================
    // DPOAE VALIDATION
    // ==========================

    const dpoaeFields = [
      formData.dpoae.left.totalOAE,
      formData.dpoae.left.percentStimulusOK,
      formData.dpoae.left.dpLevels,
      formData.dpoae.left.startFrequency,
      formData.dpoae.left.endFrequency,
      formData.dpoae.left.resolution,
      formData.dpoae.left.result,

      formData.dpoae.right.totalOAE,
      formData.dpoae.right.percentStimulusOK,
      formData.dpoae.right.dpLevels,
      formData.dpoae.right.startFrequency,
      formData.dpoae.right.endFrequency,
      formData.dpoae.right.resolution,
      formData.dpoae.right.result,
    ];

    const dpoaeStarted = dpoaeFields.some((v) => v !== undefined && v !== "");

    const dpoaeIncomplete = dpoaeFields.some(
      (v) => v === undefined || v === "",
    );

    if (dpoaeStarted && dpoaeIncomplete) {
      setErrors({
        dpoae: true,
      });

      setLoading(false);

      alert("Please complete all DPOAE fields");
      return;
    }

    // ==========================
    // TYMPANOMETRY VALIDATION
    // ==========================

    const tympFields = [
      formData.tymp.left.type,
      formData.tymp.left.earCanalVolume,
      formData.tymp.left.middleEarPressureDaPa,
      formData.tymp.left.staticCompliance,
      formData.tymp.left.result,

      formData.tymp.right.type,
      formData.tymp.right.earCanalVolume,
      formData.tymp.right.middleEarPressureDaPa,
      formData.tymp.right.staticCompliance,
      formData.tymp.right.result,
    ];

    const tympStarted = tympFields.some((v) => v !== undefined && v !== "");

    const tympIncomplete = tympFields.some((v) => v === undefined || v === "");

    if (tympStarted && tympIncomplete) {
      setErrors({
        tymp: true,
      });

      setLoading(false);

      alert("Please complete all Tympanometry fields");
      return;
    }

    // ==========================
    // AABR / BERA VALIDATION
    // ==========================

    const aabrFields = [
      formData.aabr.left.impedance,
      formData.aabr.left.stimulusLevel,
      formData.aabr.left.peakVLatency,
      formData.aabr.left.result,

      formData.aabr.right.impedance,
      formData.aabr.right.stimulusLevel,
      formData.aabr.right.peakVLatency,
      formData.aabr.right.result,
    ];

    const aabrStarted = aabrFields.some((v) => v !== undefined && v !== "");

    const aabrIncomplete = aabrFields.some((v) => v === undefined || v === "");

    if (aabrStarted && aabrIncomplete) {
      setErrors({
        aabr: true,
      });

      setLoading(false);

      alert("Please complete all AABR / BERA fields");
      return;
    }

    setLoading(true);

    try {
      const teoaeReportUrls: string[] = [...existingReportUrls.teoae];
      const dpoaeReportUrls: string[] = [...existingReportUrls.dpoae];
      const tympReportUrls: string[] = [...existingReportUrls.tymp];
      const aabrReportUrls: string[] = [...existingReportUrls.aabr];
      const deviceReportUrls: string[] = [...existingReportUrls.device];
      // --------------------------
      // 📌 1. Upload Device Reports to S3
      // --------------------------

      /* TEOAE */
      for (const file of teoaeReports) {
        const result = await uploadPdf(file, "audiology-screening/teoae");

        if (result?.key) {
          teoaeReportUrls.push(result.key);
        }
      }

      /* DPOAE */
      for (const file of dpoaeReports) {
        const result = await uploadPdf(file, "audiology-screening/dpoae");

        if (result?.key) {
          dpoaeReportUrls.push(result.key);
        }
      }

      /* TYMPANOMETRY */
      for (const file of tympReports) {
        const result = await uploadPdf(
          file,
          "audiology-screening/tympanometry",
        );

        if (result?.key) {
          tympReportUrls.push(result.key);
        }
      }

      /* AABR / BERA */
      for (const file of aabrReports) {
        const result = await uploadPdf(file, "audiology-screening/aabr-bera");

        if (result?.key) {
          aabrReportUrls.push(result.key);
        }
      }

      /* DEVICE REPORT */
      for (const file of deviceReports) {
        const result = await uploadPdf(
          file,
          "audiology-screening/device-report",
        );

        if (result?.key) {
          deviceReportUrls.push(result.key);
        }
      }

      // --------------------------
      // 📌 2. Construct payload
      // --------------------------
      const payload = {
        emrId,

        otoscopicExamination: {
          rightEar: formData.otoscopy.right
            ? {
                healthy:
                  formData.otoscopy.right.healthy === "yes"
                    ? true
                    : formData.otoscopy.right.healthy === "no"
                      ? false
                      : null,
                reasons: {
                  bulging: !!formData.otoscopy.right.reasons.bulging,
                  perforated: !!formData.otoscopy.right.reasons.perforated,
                  retracted: !!formData.otoscopy.right.reasons.retracted,
                  others: !!formData.otoscopy.right.reasons.others,
                  pleaseSpecify:
                    formData.otoscopy.right.reasons.pleaseSpecify ||
                    formData.otoscopy.right.specify ||
                    undefined,
                },
              }
            : undefined,
          leftEar: formData.otoscopy.left
            ? {
                healthy:
                  formData.otoscopy.left.healthy === "yes"
                    ? true
                    : formData.otoscopy.left.healthy === "no"
                      ? false
                      : null,
                reasons: {
                  bulging: !!formData.otoscopy.left.reasons.bulging,
                  perforated: !!formData.otoscopy.left.reasons.perforated,
                  retracted: !!formData.otoscopy.left.reasons.retracted,
                  others: !!formData.otoscopy.left.reasons.others,
                  pleaseSpecify:
                    formData.otoscopy.left.reasons.pleaseSpecify ||
                    formData.otoscopy.left.specify ||
                    undefined,
                },
              }
            : undefined,
        },

        pureToneAudiometry: formData.ptaTable.map((r) => ({
          intensity: r.intensity || "20 dBHL",
          frequency: r.frequency,
          rightEar: r.rightEar === "" ? null : r.rightEar,
          leftEar: r.leftEar === "" ? null : r.leftEar,
        })),

        audiometryResults: {
          rightEar:
            formData.audiometryResults.right === "Pass"
              ? true
              : formData.audiometryResults.right === "Fail"
                ? false
                : null,

          leftEar:
            formData.audiometryResults.left === "Pass"
              ? true
              : formData.audiometryResults.left === "Fail"
                ? false
                : null,
        },

        // ✅ Properly transformed TEOAE block
        teoaeTests: {
          leftEar: formData.teoae.left
            ? {
                totalOAE: formData.teoae.left.totalOAE
                  ? Number(formData.teoae.left.totalOAE)
                  : undefined,
                totalNoise: formData.teoae.left.totalNoise
                  ? Number(formData.teoae.left.totalNoise)
                  : undefined,
                stability: formData.teoae.left.stability
                  ? Number(formData.teoae.left.stability)
                  : undefined,
                reproducibility: formData.teoae.left.reproducibility
                  ? Number(formData.teoae.left.reproducibility)
                  : undefined,
                frequencyRange: formData.teoae.left.freqRange,
                result: formData.teoae.left.result || null,
              }
            : undefined,
          rightEar: formData.teoae.right
            ? {
                totalOAE: formData.teoae.right.totalOAE
                  ? Number(formData.teoae.right.totalOAE)
                  : undefined,
                totalNoise: formData.teoae.right.totalNoise
                  ? Number(formData.teoae.right.totalNoise)
                  : undefined,
                stability: formData.teoae.right.stability
                  ? Number(formData.teoae.right.stability)
                  : undefined,
                reproducibility: formData.teoae.right.reproducibility
                  ? Number(formData.teoae.right.reproducibility)
                  : undefined,
                frequencyRange: formData.teoae.right.freqRange,
                result: formData.teoae.right.result || null,
              }
            : undefined,
          reportUrls: teoaeReportUrls,
        },

        // ✅ Properly transformed DPOAE block
        dpoaeTests: {
          leftEar: formData.dpoae.left
            ? {
                totalOAE: formData.dpoae.left.totalOAE
                  ? Number(formData.dpoae.left.totalOAE)
                  : undefined,
                percentStimulusOK: formData.dpoae.left.percentStimulusOK
                  ? Number(formData.dpoae.left.percentStimulusOK)
                  : undefined,
                dpLevels: formData.dpoae.left.dpLevels,
                startFrequency: formData.dpoae.left.startFrequency
                  ? Number(formData.dpoae.left.startFrequency)
                  : undefined,
                endFrequency: formData.dpoae.left.endFrequency
                  ? Number(formData.dpoae.left.endFrequency)
                  : undefined,
                resolution: formData.dpoae.left.resolution,
                result: formData.dpoae.left.result || null,
              }
            : undefined,
          rightEar: formData.dpoae.right
            ? {
                totalOAE: formData.dpoae.right.totalOAE
                  ? Number(formData.dpoae.right.totalOAE)
                  : undefined,
                percentStimulusOK: formData.dpoae.right.percentStimulusOK
                  ? Number(formData.dpoae.right.percentStimulusOK)
                  : undefined,
                dpLevels: formData.dpoae.right.dpLevels,
                startFrequency: formData.dpoae.right.startFrequency
                  ? Number(formData.dpoae.right.startFrequency)
                  : undefined,
                endFrequency: formData.dpoae.right.endFrequency
                  ? Number(formData.dpoae.right.endFrequency)
                  : undefined,
                resolution: formData.dpoae.right.resolution,
                result: formData.dpoae.right.result || null,
              }
            : undefined,
          reportUrls: dpoaeReportUrls,
        },

        // ✅ Properly transformed Tympanometry block
        tympanometry: {
          leftEar: formData.tymp.left
            ? {
                type: formData.tymp.left.type,
                earCanalVolume: formData.tymp.left.earCanalVolume
                  ? Number(formData.tymp.left.earCanalVolume)
                  : undefined,
                middleEarPressureDaPa: formData.tymp.left.middleEarPressureDaPa
                  ? Number(formData.tymp.left.middleEarPressureDaPa)
                  : undefined,
                staticCompliance: formData.tymp.left.staticCompliance
                  ? Number(formData.tymp.left.staticCompliance)
                  : undefined,
                result: formData.tymp.left.result || null,
              }
            : undefined,
          rightEar: formData.tymp.right
            ? {
                type: formData.tymp.right.type,
                earCanalVolume: formData.tymp.right.earCanalVolume
                  ? Number(formData.tymp.right.earCanalVolume)
                  : undefined,
                middleEarPressureDaPa: formData.tymp.right.middleEarPressureDaPa
                  ? Number(formData.tymp.right.middleEarPressureDaPa)
                  : undefined,
                staticCompliance: formData.tymp.right.staticCompliance
                  ? Number(formData.tymp.right.staticCompliance)
                  : undefined,
                result: formData.tymp.right.result || null,
              }
            : undefined,
          reportUrls: tympReportUrls,
        },

        // ✅ Properly transformed AABR / BERA
        aabrBeraTest: {
          leftEar: formData.aabr.left
            ? {
                impedance: formData.aabr.left.impedance
                  ? Number(formData.aabr.left.impedance)
                  : undefined,
                stimulusLevel: formData.aabr.left.stimulusLevel
                  ? Number(formData.aabr.left.stimulusLevel)
                  : undefined,
                peakVLatency: formData.aabr.left.peakVLatency
                  ? Number(formData.aabr.left.peakVLatency)
                  : undefined,
                result: formData.aabr.left.result || null,
              }
            : undefined,
          rightEar: formData.aabr.right
            ? {
                impedance: formData.aabr.right.impedance
                  ? Number(formData.aabr.right.impedance)
                  : undefined,
                stimulusLevel: formData.aabr.right.stimulusLevel
                  ? Number(formData.aabr.right.stimulusLevel)
                  : undefined,
                peakVLatency: formData.aabr.right.peakVLatency
                  ? Number(formData.aabr.right.peakVLatency)
                  : undefined,
                result: formData.aabr.right.result || null,
              }
            : undefined,
          reportUrls: aabrReportUrls,
        },
        // ✅ Final Summary
        finalSummary: {
          rightEar: formData.summary.right || null,
          leftEar: formData.summary.left || null,
        },

        finalRecommendation: finalRecommendation.trim(),
        // -----------------------------
        // 📌 S3 URLs INTO MONGO
        // -----------------------------

        // deviceReportUrls: uploadedUrls,
        deviceReportUrls,
      };

      console.log("📦 Submitting payload:", payload);

      const res = await api.dataCollectionApi.post(
        "api/forms/audiology",
        payload,
      );

      if (res.data?.success) {
        alert("Audiology Screening submitted successfully!");

        onComplete && onComplete();

        resetForm();
      }
    } catch (e) {
      console.error(e);
      alert("Error submitting form");
    } finally {
      setLoading(false);
    }
  };

  const resetPta = () => {
    setFormData((prev) => ({
      ...prev,
      ptaTable: prev.ptaTable.map((row) => ({
        ...row,
        rightEar: "",
        leftEar: "",
      })),
    }));

    setPtaReports([]);
  };

  const resetTeoae = () => {
    setFormData((prev) => ({
      ...prev,
      teoae: {
        left: {
          totalOAE: "",
          totalNoise: "",
          stability: "",
          reproducibility: "",
          freqRange: "",
          result: "",
        },
        right: {
          totalOAE: "",
          totalNoise: "",
          stability: "",
          reproducibility: "",
          freqRange: "",
          result: "",
        },
      },
    }));

    setTeoaeReports([]);
  };

  const resetDpoae = () => {
    setFormData((prev) => ({
      ...prev,
      dpoae: {
        left: {
          totalOAE: "",
          percentStimulusOK: "",
          dpLevels: "",
          startFrequency: "",
          endFrequency: "",
          resolution: "",
          result: "",
        },
        right: {
          totalOAE: "",
          percentStimulusOK: "",
          dpLevels: "",
          startFrequency: "",
          endFrequency: "",
          resolution: "",
          result: "",
        },
      },
    }));

    setDpoaeReports([]);
  };

  const resetTymp = () => {
    setFormData((prev) => ({
      ...prev,
      tymp: {
        left: {
          type: "",
          earCanalVolume: "",
          middleEarPressureDaPa: "",
          staticCompliance: "",
          result: "",
        },
        right: {
          type: "",
          earCanalVolume: "",
          middleEarPressureDaPa: "",
          staticCompliance: "",
          result: "",
        },
      },
    }));

    setTympReports([]);
  };

  const resetAabr = () => {
    setFormData((prev) => ({
      ...prev,
      aabr: {
        left: {
          impedance: "",
          stimulusLevel: "",
          peakVLatency: "",
          result: "",
        },
        right: {
          impedance: "",
          stimulusLevel: "",
          peakVLatency: "",
          result: "",
        },
      },
    }));

    setAabrReports([]);
  };

  return (
    <form className="audio-form" onSubmit={handleSubmit}>
      <div className="vs-section-bar">Audiology Screening</div>

      {/* === Otoscope Examination === */}
      <div
        className={`audio-card ${errors.otoscopy ? "audio-error-card" : ""}`}
      >
        <div
          className="audio-card-title"
          onClick={() => toggleSection("otoscopy")}
          style={{
            cursor: "pointer",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>Otoscope Examination</span>
          <span>{expandedSections.otoscopy ? "▲" : "▼"}</span>
        </div>

        {expandedSections.otoscopy && (
          <>
            <div className="audio-ears">
              {(["right", "left"] as EarSide[]).map((ear) => (
                <div className="audio-ear-card" key={ear}>
                  <h4>{ear === "right" ? "Right Ear" : "Left Ear"}</h4>
                  <label>Healthy?</label>
                  <div className="audio-radio-group">
                    <label>
                      <input
                        type="radio"
                        name={`${ear}-healthy`}
                        checked={formData.otoscopy[ear].healthy === "yes"}
                        onChange={() => setOtoHealthy(ear, "yes")}
                      />{" "}
                      Yes
                    </label>
                    <label>
                      <input
                        type="radio"
                        name={`${ear}-healthy`}
                        checked={formData.otoscopy[ear].healthy === "no"}
                        onChange={() => setOtoHealthy(ear, "no")}
                      />{" "}
                      No
                    </label>
                  </div>

                  {/* Show only when Healthy = No */}
                  {formData.otoscopy[ear].healthy === "no" && (
                    <>
                      <label>If “No”, Reasons</label>

                      <div className="audio-checkbox-group">
                        {(
                          [
                            "bulging",
                            "perforated",
                            "retracted",
                            "others",
                          ] as (keyof Otoscopy["reasons"])[]
                        ).map((r) => (
                          <label key={r}>
                            <input
                              type="checkbox"
                              checked={!!formData.otoscopy[ear].reasons[r]}
                              onChange={() => toggleOtoReason(ear, r)}
                            />{" "}
                            {r.charAt(0).toUpperCase() + r.slice(1)}
                          </label>
                        ))}
                      </div>

                      {/* Show textbox only when Others checked */}
                      {formData.otoscopy[ear].reasons.others && (
                        <input
                          type="text"
                          placeholder="Please Specify"
                          value={formData.otoscopy[ear].specify || ""}
                          onChange={(e) => setOtoSpecify(ear, e.target.value)}
                        />
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* === PTA Section === */}
      <div className={`audio-card ${errors.pta ? "audio-error-card" : ""}`}>
        <div
          className="audio-card-title"
          onClick={() => toggleSection("pta")}
          style={{
            cursor: "pointer",
            display: "flex",
            // justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>Pure Tone Audiometry </span>

          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <button
              type="button"
              className="audio-reset-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                resetPta();
              }}
            >
              Reset
            </button>
          </div>

          <span>{expandedSections.pta ? "▲" : "▼"}</span>
        </div>

        {expandedSections.pta && (
          <>
            <div className="audio-table">
              <div className="audio-table-header">
                <span>Intensity</span>
                <span>Frequency</span>
                <span>Right Ear</span>
                <span>Left Ear</span>
              </div>

              {formData.ptaTable.map((row, idx) => {
                const prevIntensity =
                  idx > 0 ? formData.ptaTable[idx - 1].intensity : "";
                const showDivider =
                  row.intensity !== prevIntensity && idx !== 0;

                return (
                  <React.Fragment key={idx}>
                    {showDivider && (
                      <div className="audio-intensity-divider"></div>
                    )}

                    <div className="audio-table-row">
                      {/* Intensity */}
                      <span>{row.intensity}</span>

                      {/* Frequency */}
                      <span>{row.frequency}</span>

                      {/* Right Ear */}
                      <div className="audio-radio-group">
                        <label>
                          <input
                            type="radio"
                            name={`ptaR-${idx}`}
                            checked={row.rightEar === "Pass"}
                            onChange={() => setPta(idx, "rightEar", "Pass")}
                          />{" "}
                          Pass
                        </label>
                        <label>
                          <input
                            type="radio"
                            name={`ptaR-${idx}`}
                            checked={row.rightEar === "Fail"}
                            onChange={() => setPta(idx, "rightEar", "Fail")}
                          />{" "}
                          Fail
                        </label>
                      </div>

                      {/* Left Ear */}
                      <div className="audio-radio-group">
                        <label>
                          <input
                            type="radio"
                            name={`ptaL-${idx}`}
                            checked={row.leftEar === "Pass"}
                            onChange={() => setPta(idx, "leftEar", "Pass")}
                          />{" "}
                          Pass
                        </label>
                        <label>
                          <input
                            type="radio"
                            name={`ptaL-${idx}`}
                            checked={row.leftEar === "Fail"}
                            onChange={() => setPta(idx, "leftEar", "Fail")}
                          />{" "}
                          Fail
                        </label>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>

            <div className="audio-card" style={{ marginTop: "16px" }}>
              <div className="audio-card-title">
                <span>Audiometry Results</span>
              </div>

              <div className="audio-ears">
                <div className="audio-ear-card teoae-ear-card">
                  <div className="ear-header">
                    <span>Right Ear</span>
                  </div>

                  <div className="audio-radio-group teoae-radio">
                    <label>
                      <input
                        type="radio"
                        name="audiometry-result-right"
                        checked={formData.audiometryResults.right === "Pass"}
                        onChange={() => setAudiometryResult("right", "Pass")}
                      />{" "}
                      Pass
                    </label>

                    <label>
                      <input
                        type="radio"
                        name="audiometry-result-right"
                        checked={formData.audiometryResults.right === "Fail"}
                        onChange={() => setAudiometryResult("right", "Fail")}
                      />{" "}
                      Fail
                    </label>
                  </div>
                </div>

                <div className="audio-ear-card teoae-ear-card">
                  <div className="ear-header">
                    <span>Left Ear</span>
                  </div>

                  <div className="audio-radio-group teoae-radio">
                    <label>
                      <input
                        type="radio"
                        name="audiometry-result-left"
                        checked={formData.audiometryResults.left === "Pass"}
                        onChange={() => setAudiometryResult("left", "Pass")}
                      />{" "}
                      Pass
                    </label>

                    <label>
                      <input
                        type="radio"
                        name="audiometry-result-left"
                        checked={formData.audiometryResults.left === "Fail"}
                        onChange={() => setAudiometryResult("left", "Fail")}
                      />{" "}
                      Fail
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="audio-upload-mini">
              <div className="audio-card-title">
                Pure Tone Audiometry Report Upload
              </div>

              <div className="audio-upload-mini-box">
                <div className="audio-upload-mini-content">
                  <div className="audio-upload-mini-icon">☁️</div>

                  <p className="audio-upload-mini-text">Upload PTA Report</p>

                  <button
                    type="button"
                    className="audio-upload-mini-btn"
                    onClick={() =>
                      document.getElementById("ptaUpload")?.click()
                    }
                  >
                    Click to Upload
                  </button>

                  <small className="audio-upload-mini-hint">
                    Maximum file size: 10MB (Multiple files allowed) PDF only
                  </small>

                  {existingReportUrls.device.map((url, i) => (
                    <div key={`existing-pta-${i}`} className="audio-file-chip">
                      <span>📄 {getExistingFileName(url)}</span>
                    </div>
                  ))}

                  {ptaReports.map((file, i) => (
                    <div key={i} className="audio-file-chip">
                      <span>{file.name}</span>

                      <button
                        type="button"
                        className="audio-file-remove"
                        onClick={() => removeSectionFile(i, setPtaReports)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}

                  <input
                    id="ptaUpload"
                    type="file"
                    accept=".pdf,image/*"
                    multiple
                    hidden
                    onChange={(e) => handleSectionFilePick(e, setPtaReports)}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* === TEOAE Section === */}

      <div className={`audio-card ${errors.teoae ? "audio-error-card" : ""}`}>
        {/* <div
          className="audio-card-title"
          onClick={() => toggleSection("teoae")}
          style={{
            cursor: "pointer",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>TEOAE Tests</span>
          <span>{expandedSections.teoae ? "▲" : "▼"}</span>
        </div> */}

        <div
          className="audio-card-title"
          onClick={() => toggleSection("teoae")}
          style={{
            cursor: "pointer",
            display: "flex",
            // justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>TEOAE Tests</span>

          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                resetTeoae();
              }}
              className="audio-reset-btn"
            >
              Reset
            </button>
          </div>

          <span>{expandedSections.teoae ? "▲" : "▼"}</span>
        </div>

        {expandedSections.teoae && (
          <>
            <div className="audio-ears">
              {(["left", "right"] as EarSide[]).map((ear) => (
                <div className="audio-ear-card teoae-ear-card" key={ear}>
                  <div className="ear-header">
                    <span>{ear === "left" ? "Left Ear" : "Right Ear"}</span>
                  </div>

                  <div className="teoae-fields">
                    {[
                      { key: "totalOAE", label: "Total OAE" },
                      { key: "totalNoise", label: "Total Noise" },
                      { key: "stability", label: "Stability" },
                      { key: "reproducibility", label: "Reproducibility" },
                      { key: "freqRange", label: "Frequency Range" },
                    ].map((f) => (
                      <div className="teoae-field" key={f.key}>
                        <label>{f.label}</label>
                        <input
                          type="text"
                          placeholder={f.label}
                          value={(formData.teoae[ear] as any)[f.key] || ""}
                          onChange={(e) =>
                            setOAEField("teoae", ear, f.key, e.target.value)
                          }
                        />
                      </div>
                    ))}
                  </div>

                  <div className="audio-radio-group teoae-radio">
                    <label>
                      <input
                        type="radio"
                        name={`teoae-${ear}`}
                        checked={formData.teoae[ear].result === "Pass"}
                        onChange={() => setOAEResult("teoae", ear, "Pass")}
                      />{" "}
                      Pass
                    </label>
                    <label>
                      <input
                        type="radio"
                        name={`teoae-${ear}`}
                        checked={formData.teoae[ear].result === "Fail"}
                        onChange={() => setOAEResult("teoae", ear, "Fail")}
                      />{" "}
                      Fail
                    </label>
                  </div>
                </div>
              ))}
            </div>
            <div className="audio-upload-mini">
              <div className="audio-card-title">TEOAE Report Upload</div>

              <div
                className="audio-upload-mini-box"
                // onClick={() => document.getElementById("teoaeUpload")?.click()}
              >
                <div className="audio-upload-mini-content">
                  <div className="audio-upload-mini-icon">☁️</div>

                  <p className="audio-upload-mini-text">Upload TEOAE Report</p>

                  <button
                    type="button"
                    className="audio-upload-mini-btn"
                    onClick={() =>
                      document.getElementById("teoaeUpload")?.click()
                    }
                  >
                    Click to Upload
                  </button>

                  <small className="audio-upload-mini-hint">
                    Maximum file size: 10MB (Multiple files allowed) PDF only
                  </small>

                  {existingReportUrls.teoae.map((url, i) => (
                    <div
                      key={`existing-teoae-${i}`}
                      className="audio-file-chip"
                    >
                      <span>📄 {getExistingFileName(url)}</span>
                    </div>
                  ))}

                  {teoaeReports.map((file, i) => (
                    <div key={i} className="audio-file-chip">
                      <span>{file.name}</span>

                      <button
                        type="button"
                        className="audio-file-remove"
                        onClick={() => removeSectionFile(i, setTeoaeReports)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}

                  <input
                    id="teoaeUpload"
                    type="file"
                    accept=".pdf,image/*"
                    multiple
                    hidden
                    onChange={(e) => handleSectionFilePick(e, setTeoaeReports)}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* === DPOAE Section === */}
      <div className={`audio-card ${errors.dpoae ? "audio-error-card" : ""}`}>
        {/* <div className="audio-card-title">DPOAE Tests</div> */}

        <div
          className="audio-card-title"
          style={{
            cursor: "pointer",
            display: "flex",
            // justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>DPOAE Tests</span>

          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <button
              type="button"
              className="audio-reset-btn"
              onClick={(e) => {
                e.stopPropagation();
                resetDpoae();
              }}
            >
              Reset
            </button>
          </div>

          <span onClick={() => toggleSection("dpoae")}>
            {expandedSections.dpoae ? "▲" : "▼"}
          </span>
        </div>

        {expandedSections.dpoae && (
          <>
            <div className="audio-ears">
              {(["left", "right"] as EarSide[]).map((ear) => (
                <div className="audio-ear-card teoae-ear-card" key={ear}>
                  <div className="ear-header">
                    <span>{ear === "left" ? "Left Ear" : "Right Ear"}</span>
                  </div>

                  <div className="teoae-fields">
                    {[
                      { key: "totalOAE", label: "Total OAE" },
                      { key: "percentStimulusOK", label: "% Stimulus OK" },
                      { key: "dpLevels", label: "DP Levels" },
                      { key: "startFrequency", label: "Start Frequency" },
                      { key: "endFrequency", label: "End Frequency" },
                      { key: "resolution", label: "Resolution" },
                    ].map((f) => (
                      <div className="teoae-field" key={f.key}>
                        <label>{f.label}</label>
                        <input
                          type="text"
                          placeholder={f.label}
                          value={(formData.dpoae[ear] as any)[f.key] || ""}
                          onChange={(e) =>
                            setOAEField("dpoae", ear, f.key, e.target.value)
                          }
                        />
                      </div>
                    ))}
                  </div>

                  <div className="audio-radio-group teoae-radio">
                    <label>
                      <input
                        type="radio"
                        name={`dpoae-${ear}`}
                        checked={formData.dpoae[ear].result === "Pass"}
                        onChange={() => setOAEResult("dpoae", ear, "Pass")}
                      />{" "}
                      Pass
                    </label>
                    <label>
                      <input
                        type="radio"
                        name={`dpoae-${ear}`}
                        checked={formData.dpoae[ear].result === "Fail"}
                        onChange={() => setOAEResult("dpoae", ear, "Fail")}
                      />{" "}
                      Fail
                    </label>
                  </div>
                </div>
              ))}
            </div>
            <div className="audio-upload-mini">
              <div className="audio-card-title">DPOAE Report Upload</div>

              <div className="audio-upload-mini-box">
                <div className="audio-upload-mini-content">
                  <div className="audio-upload-mini-icon">☁️</div>

                  <p className="audio-upload-mini-text">Upload DPOAE Report</p>

                  <button
                    type="button"
                    className="audio-upload-mini-btn"
                    onClick={() =>
                      document.getElementById("dpoaeUpload")?.click()
                    }
                  >
                    Click to Upload
                  </button>

                  <small className="audio-upload-mini-hint">
                    Maximum file size: 10MB (Multiple files allowed) PDF only
                  </small>

                  {existingReportUrls.dpoae.map((url, i) => (
                    <div
                      key={`existing-dpoae-${i}`}
                      className="audio-file-chip"
                    >
                      <span>📄 {getExistingFileName(url)}</span>
                    </div>
                  ))}

                  {dpoaeReports.map((file, i) => (
                    <div key={i} className="audio-file-chip">
                      <span>{file.name}</span>

                      <button
                        type="button"
                        className="audio-file-remove"
                        onClick={() => removeSectionFile(i, setDpoaeReports)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}

                  <input
                    id="dpoaeUpload"
                    type="file"
                    // accept="application/pdf"
                    accept=".pdf,image/*"
                    multiple
                    hidden
                    onChange={(e) => handleSectionFilePick(e, setDpoaeReports)}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* === Tympanometry Section === */}
      <div className={`audio-card ${errors.tymp ? "audio-error-card" : ""}`}>
        {/* <div className="audio-card-title">Tympanometry</div> */}

        <div
          className="audio-card-title"
          style={{
            cursor: "pointer",
            display: "flex",
            // justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>Tympanometry</span>

          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <button
              type="button"
              className="audio-reset-btn"
              onClick={(e) => {
                e.stopPropagation();
                resetTymp();
              }}
            >
              Reset
            </button>
          </div>

          <span onClick={() => toggleSection("tymp")}>
            {expandedSections.tymp ? "▲" : "▼"}
          </span>
        </div>

        {expandedSections.tymp && (
          <>
            <div className="audio-ears">
              {(["left", "right"] as EarSide[]).map((ear) => (
                <div className="audio-ear-card teoae-ear-card" key={ear}>
                  <div className="ear-header">
                    <span>{ear === "left" ? "Left Ear" : "Right Ear"}</span>
                  </div>

                  <div className="teoae-fields">
                    {[
                      { key: "type", label: "Type" },
                      { key: "earCanalVolume", label: "Ear Canal Volume" },
                      {
                        key: "middleEarPressureDaPa",
                        label: "Middle Ear Pressure (daPa)",
                      },
                      { key: "staticCompliance", label: "Static Compliance" },
                    ].map((f) => (
                      <div className="teoae-field" key={f.key}>
                        <label>{f.label}</label>
                        <input
                          type="text"
                          placeholder={f.label}
                          value={(formData.tymp[ear] as any)[f.key] || ""}
                          onChange={(e) =>
                            setTympField(ear, f.key as any, e.target.value)
                          }
                        />
                      </div>
                    ))}
                  </div>

                  <div className="audio-radio-group teoae-radio">
                    <label>
                      <input
                        type="radio"
                        name={`tymp-${ear}`}
                        checked={formData.tymp[ear].result === "Pass"}
                        onChange={() => setTympResult(ear, "Pass")}
                      />{" "}
                      Pass
                    </label>
                    <label>
                      <input
                        type="radio"
                        name={`tymp-${ear}`}
                        checked={formData.tymp[ear].result === "Fail"}
                        onChange={() => setTympResult(ear, "Fail")}
                      />{" "}
                      Fail
                    </label>
                  </div>
                </div>
              ))}
            </div>
            <div className="audio-upload-mini">
              <div className="audio-card-title">Tympanometry Report Upload</div>

              <div className="audio-upload-mini-box">
                <div className="audio-upload-mini-content">
                  <div className="audio-upload-mini-icon">☁️</div>

                  <p className="audio-upload-mini-text">
                    Upload Tympanometry Report
                  </p>

                  <button
                    type="button"
                    className="audio-upload-mini-btn"
                    onClick={() =>
                      document.getElementById("tympUpload")?.click()
                    }
                  >
                    Click to Upload
                  </button>

                  <small className="audio-upload-mini-hint">
                    {" "}
                    Maximum file size: 10MB (Multiple files allowed) PDF only
                  </small>

                  {existingReportUrls.tymp.map((url, i) => (
                    <div key={`existing-tymp-${i}`} className="audio-file-chip">
                      <span>📄 {getExistingFileName(url)}</span>
                    </div>
                  ))}

                  {tympReports.map((file, i) => (
                    <div key={i} className="audio-file-chip">
                      <span>{file.name}</span>

                      <button
                        type="button"
                        className="audio-file-remove"
                        onClick={() => removeSectionFile(i, setTympReports)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}

                  <input
                    id="tympUpload"
                    type="file"
                    // accept="application/pdf"
                    accept=".pdf,image/*"
                    multiple
                    hidden
                    onChange={(e) => handleSectionFilePick(e, setTympReports)}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* === AABR / BERA Section === */}

      <div className={`audio-card ${errors.aabr ? "audio-error-card" : ""}`}>
        {/* <div className="audio-card-title">AABR / BERA Test</div> */}

        <div
          className="audio-card-title"
          style={{
            cursor: "pointer",
            display: "flex",
            // justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>AABR / BERA Test</span>

          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <button
              type="button"
              className="audio-reset-btn"
              onClick={(e) => {
                e.stopPropagation();
                resetAabr();
              }}
            >
              Reset
            </button>
          </div>

          <span onClick={() => toggleSection("aabr")}>
            {expandedSections.aabr ? "▲" : "▼"}
          </span>
        </div>

        {expandedSections.aabr && (
          <>
            <div className="audio-ears">
              {(["left", "right"] as EarSide[]).map((ear) => (
                <div className="audio-ear-card teoae-ear-card" key={ear}>
                  <div className="ear-header">
                    <span>{ear === "left" ? "Left Ear" : "Right Ear"}</span>
                  </div>

                  <div className="teoae-fields">
                    {[
                      { key: "impedance", label: "Impedance" },
                      { key: "stimulusLevel", label: "Stimulus Level" },
                      { key: "peakVLatency", label: "Peak V Latency" },
                    ].map((f) => (
                      <div className="teoae-field" key={f.key}>
                        <label>{f.label}</label>
                        <input
                          type="text"
                          placeholder={f.label}
                          value={(formData.aabr[ear] as any)[f.key] || ""}
                          onChange={(e) =>
                            setAabrField(ear, f.key as any, e.target.value)
                          }
                        />
                      </div>
                    ))}
                  </div>

                  <div className="audio-radio-group teoae-radio">
                    <label>
                      <input
                        type="radio"
                        name={`aabr-${ear}`}
                        checked={formData.aabr[ear].result === "Pass"}
                        onChange={() => setAabrResult(ear, "Pass")}
                      />{" "}
                      Pass
                    </label>
                    <label>
                      <input
                        type="radio"
                        name={`aabr-${ear}`}
                        checked={formData.aabr[ear].result === "Fail"}
                        onChange={() => setAabrResult(ear, "Fail")}
                      />{" "}
                      Fail
                    </label>
                  </div>
                </div>
              ))}
            </div>
            <div className="audio-upload-mini">
              <div className="audio-card-title">AABR / BERA Report Upload</div>

              <div className="audio-upload-mini-box">
                <div className="audio-upload-mini-content">
                  <div className="audio-upload-mini-icon">☁️</div>

                  <p className="audio-upload-mini-text">
                    Upload AABR / BERA Report
                  </p>

                  <button
                    type="button"
                    className="audio-upload-mini-btn"
                    onClick={() =>
                      document.getElementById("aabrUpload")?.click()
                    }
                  >
                    Click to Upload
                  </button>

                  <small className="audio-upload-mini-hint">
                    {" "}
                    Maximum file size: 10MB (Multiple files allowed) PDF only
                  </small>

                  {existingReportUrls.aabr.map((url, i) => (
                    <div key={`existing-aabr-${i}`} className="audio-file-chip">
                      <span>📄 {getExistingFileName(url)}</span>
                    </div>
                  ))}

                  {aabrReports.map((file, i) => (
                    <div key={i} className="audio-file-chip">
                      <span>{file.name}</span>

                      <button
                        type="button"
                        className="audio-file-remove"
                        onClick={() => removeSectionFile(i, setAabrReports)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}

                  <input
                    id="aabrUpload"
                    type="file"
                    // accept="application/pdf"
                    accept=".pdf,image/*"
                    multiple
                    hidden
                    onChange={(e) => handleSectionFilePick(e, setAabrReports)}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* === Final Summary === */}

      <div className={`audio-card ${errors.summary ? "audio-error-card" : ""}`}>
        {/* <div className="audio-card-title">Final Summary & Recommendation</div> */}

        <div
          className="audio-card-title"
          onClick={() => toggleSection("summary")}
          style={{
            cursor: "pointer",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>Final Summary & Recommendation</span>
          <span>{expandedSections.summary ? "▲" : "▼"}</span>
        </div>

        {expandedSections.summary && (
          <>
            <div className="audio-ears">
              {(["right", "left"] as EarSide[]).map((ear) => (
                <div className="audio-ear-card" key={ear}>
                  <h4>{ear === "right" ? "Right Ear" : "Left Ear"}</h4>
                  <div className="teoae-field">
                    <h4> Summary Decision </h4>{" "}
                  </div>
                  <div className="audio-radio-group">
                    {["Pass", "Fail", "Rescreen"].map((v) => (
                      <label key={v}>
                        <input
                          type="radio"
                          name={`sum-${ear}`}
                          checked={formData.summary[ear] === v}
                          onChange={() => setSummary(ear, v as any)}
                        />{" "}
                        {v}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="audio-protocol">
              <strong>Protocol:</strong>
              <ul>
                <li>If the child fails OAE or DPOAE → Refer</li>
                <li>If fails OAE/DP and passes Tymp → Fail</li>
                <li>If fails all three → Rescreen</li>
              </ul>
            </div>

            <div className="audio-final-recommendation">
              <label htmlFor="finalRecommendation">Final Recommendation</label>

              <textarea
                id="finalRecommendation"
                value={finalRecommendation}
                onChange={(e) => setFinalRecommendation(e.target.value)}
                placeholder="Enter final recommendation (optional)"
                rows={4}
              />
            </div>
          </>
        )}
      </div>

      <div className="audio-actions">
        <button type="button" className="audio-btn audio-btn-secondary">
          Close
        </button>
        <button
          type="submit"
          className="audio-btn audio-btn-primary"
          disabled={loading || s3Uploading}
        >
          {loading || s3Uploading ? "Submitting..." : "Submit Assessment"}
        </button>
      </div>
    </form>
  );
};

export default AudiologyScreeningForm;
